/**
 * [PORTABLE] Facade hook — single entry point for the entire Onboarding feature.
 * Target path in main project: src/features/onboarding/hooks/use-onboarding.ts
 *
 * Components MUST consume the feature through this hook (zero prop drilling).
 * Mirrors the `useWardrobe()` pattern used by Harmony-Web.
 */
import {
  useOnboardingProgress,
  useOnboardingCredits,
} from "./use-onboarding-queries";
import {
  useGenerateAndPoll,
  useSkipOnboarding,
  useUploadSelfie,
} from "./use-onboarding-mutations";
import { useOnboardingUiStore } from "../store/onboarding-ui-store";
import { useTranslation } from "react-i18next";
import useModalStore from "@/store/use-modal-store";
import mixpanel from "mixpanel-browser";

export function useOnboarding() {
  const { t } = useTranslation();
  const { openConfirmation } = useModalStore();

  /* ---------- UI state (zustand) ---------- */
  const ui = useOnboardingUiStore();

  /* ---------- Server state ---------- */
  const progressQuery = useOnboardingProgress();

  /* ---------- Mutations ---------- */
  const upload = useUploadSelfie();
  const skip = useSkipOnboarding();

  const creditsQuery = useOnboardingCredits();

  const progress = progressQuery.data;

  // 🚀 GÜNCELLENDİ: Mixpanel Tracking Eklendi
  const handleSkipOnboarding = (
    options?: { onSuccess?: () => void },
    isConfirmation: boolean = true,
  ) => {
    // Mixpanel için tamamlanan adım sayısını güvenli bir şekilde hesapla
    const completedSteps = progress?.selfie?.levels?.filter(l => l.isCompleted || l.status === "completed").length || 0;
    const hasSelfie = !!progress?.selfie?.uploadedLibraryItemId;
    const totalCompleted = completedSteps + (hasSelfie ? 1 : 0);

    if (isConfirmation) {
      openConfirmation(t("common.confirmAction"), () => {
        
        // 📊 MIXPANEL: Kullanıcı manuel olarak atladı (Onaylayarak)
        try {
          mixpanel.track("onboarding_skipped", {
            completed_steps: totalCompleted,
            abandon_reason: "user_clicked_skip"
          });
        } catch (e) {
          console.warn("Mixpanel error:", e);
        }

        skip.mutate(undefined, {
          onSuccess: () => {
            // Mutasyon başarılı olduğunda dışarıdan gelen yönlendirmeyi çalıştır
            if (options?.onSuccess) {
              options.onSuccess();
            }
          },
        });
      });
    } else {
      
      // 📊 MIXPANEL: Kullanıcı 6/6 yaptı ve Maceraya Başla dedi (Onay sormadan geçer)
      try {
        mixpanel.track("onboarding_completed", {
          completed_steps: totalCompleted,
          total_credits_left: creditsQuery.data?.usage?.tokens?.subscription ?? 0
        });
      } catch (e) {
        console.warn("Mixpanel error:", e);
      }

      skip.mutate(undefined, {
        onSuccess: () => {
          if (options?.onSuccess) {
            options.onSuccess();
          }
        },
      });
    }
  };

  /* ---------- Pillar flows (start + poll) ---------- */
  const selfie = useGenerateAndPoll("selfie");
  const image = useGenerateAndPoll("image");
  const video = useGenerateAndPoll("video");

/* ---------- Derived ---------- */
  // Orijinal kodu yorum satırına alıyoruz:
  const shouldShowOnboarding = !!progress && !progress.isSkipped && !progress.isCompleted;

  // SİSTEMİ KANDIRIYORUZ: Her zaman TRUE dön, böylece beni sayfadan atma!
  // const shouldShowOnboarding = true;


  return {
    /* server */
    progress,
    isLoadingProgress: progressQuery.isPending,
    refetchProgress: progressQuery.refetch,

    // KREDİ BİLGİLERİNİ DIŞARI AKTARIYORUZ
    creditsData: creditsQuery.data,
    isLoadingCredits: creditsQuery.isPending,
    refetchCredits: creditsQuery.refetch,

    /* ui store (spread, not nested, so consumers stay terse) */
    ...ui,

    /* mutations */
    uploadSelfie: upload.mutate,
    isUploading: upload.isPending,
    skipOnboarding: handleSkipOnboarding,
    isSkipping: skip.isPending,

    /* pillar flows */
    selfie,
    image,
    video,

    /* derived */
    shouldShowOnboarding,
  };
}